import { customPaymentMethods } from "@jti/fe-common";
import {
  fromMagentoOrderAddress,
  type ICurrencyEnum,
  type IMoney,
  normalizeProductNicotineStrengthData,
  type Order,
  type OrderPrices,
  type SelectedBundleOption,
} from "@jti/magento";
import type { MagentoApiGetCustomerOrderQueryResponse } from "@jti/magento-api-v2";

type CustomerOrderResponse = NonNullable<
  NonNullable<NonNullable<MagentoApiGetCustomerOrderQueryResponse["customer"]>["orders"]>["items"][number]
>;

type CustomerOrderItemResponse = NonNullable<NonNullable<CustomerOrderResponse["items"]>[number]>;
type CustomerOrderTotalResponse = NonNullable<CustomerOrderResponse["total"]>;
type LegacyPrice = NonNullable<Order["total_after_trial"]>["total"];
type LegacyOrderItem = NonNullable<Order["items"]>[number];

const isNonNullable = <T>(value: T): value is NonNullable<T> => value != null;

const getRequiredValue = <T>(value: T | null | undefined, errorCode: string): NonNullable<T> => {
  if (value == null) {
    throw new Error(errorCode);
  }

  return value;
};

const getFallbackCurrency = (
  ...currencies: Array<LegacyPrice["currency"] | null | undefined>
): LegacyPrice["currency"] => getRequiredValue(currencies.find(isNonNullable), "ORDER_CURRENCY_MISSING");

const toLegacyPrice = (
  money: { currency?: LegacyPrice["currency"] | null; value?: number | null } | null | undefined,
  fallbackCurrency: LegacyPrice["currency"]
): LegacyPrice => ({
  value: money?.value ?? 0,
  currency: money?.currency ?? fallbackCurrency,
});

const toIMoney = (
  money: { currency?: LegacyPrice["currency"] | null; value?: number | null } | null | undefined,
  fallbackCurrency: LegacyPrice["currency"]
): IMoney => ({
  value: money?.value ?? 0,
  currency: (money?.currency ?? fallbackCurrency) as ICurrencyEnum,
});

const mapBundleOptions = (item: CustomerOrderItemResponse): SelectedBundleOption[] | undefined => {
  if (!("bundle_options" in item) || !item.bundle_options) {
    return undefined;
  }

  return item.bundle_options.filter(isNonNullable).map((option) => ({
    id: option.id,
    label: option.label,
    values: option.values?.filter(isNonNullable),
  })) as SelectedBundleOption[];
};

const mapTrialTotals = (
  trialTotals: CustomerOrderResponse["total_after_trial"] | CustomerOrderResponse["total_including_trial"],
  errorCode: string,
  fallbackCurrency: LegacyPrice["currency"]
) => {
  const requiredTrialTotals = getRequiredValue(trialTotals, errorCode);

  return {
    total: toLegacyPrice(requiredTrialTotals.total, fallbackCurrency),
    total_incl_tax: toLegacyPrice(requiredTrialTotals.total_incl_tax, fallbackCurrency),
    tax: toLegacyPrice(requiredTrialTotals.tax, fallbackCurrency),
  };
};

const mapAppliedCampaign = (
  appliedCampaign: CustomerOrderResponse["loyalty_info"]
): OrderPrices["appliedCampaign"] => ({
  loyalty_discount_amount: appliedCampaign?.loyalty_discount_amount ?? 0,
  loyalty_discount_points: appliedCampaign?.loyalty_discount_points ?? undefined,
  loyalty_discount_uuid: appliedCampaign?.loyalty_discount_uuid ?? "",
});

const mapOrderItem = (item: CustomerOrderItemResponse, showPricesWithTaxExcluded = false): LegacyOrderItem => {
  const product = getRequiredValue(item.product, "ORDER_ITEM_PRODUCT_MISSING");
  const fallbackCurrency = getFallbackCurrency(
    item.row_total_incl_tax.currency,
    item.row_total.currency,
    item.product_sale_price.currency,
    item.product_sale_price_incl_tax.currency
  );

  return {
    item_id: item.id,
    bundle_options: mapBundleOptions(item),
    sku: item.product_sku,
    name: item.product_name ?? "",
    price: toLegacyPrice(showPricesWithTaxExcluded ? item.row_total : item.row_total_incl_tax, fallbackCurrency),
    productSalePrice: toLegacyPrice(item.product_sale_price, fallbackCurrency),
    productSalePriceInclTax: toLegacyPrice(item.product_sale_price_incl_tax, fallbackCurrency),
    quantity: item.quantity_ordered ?? 0,
    isGift: item.gift_data?.is_gift ?? false,
    parentGiftSku: item.gift_data?.parent_gift_sku ?? "",
    enable_order_quantity_limit: product.enable_order_quantity_limit ?? 0,
    order_quantity_limit: product.order_quantity_limit ?? 0,
    imageUrl: product.image?.url ?? "",
    categories: product.categories?.filter(isNonNullable).map((category) => ({
      name: category.name ?? "",
      uid: category.uid,
    })),
    product_type: item.product_type ?? undefined,
    description: product.description?.html ?? "",
    short_description: product.short_description?.html ?? undefined,
    rules: [],
    price_range: product.price_range,
    subscription: product.subscription ?? undefined,
    subscription_format: product.subscription_format ?? undefined,
    nicotineStrength: normalizeProductNicotineStrengthData(product.strength_details),
  } as LegacyOrderItem;
};

const mapOrderPrices = (
  orderTotal: CustomerOrderTotalResponse,
  order: CustomerOrderResponse,
  fallbackCurrency: LegacyPrice["currency"]
): OrderPrices => ({
  grandTotal: toIMoney(orderTotal.grand_total, fallbackCurrency),
  subtotal: toIMoney(orderTotal.subtotal, fallbackCurrency),
  subtotalIncludingTax: toIMoney(orderTotal.subtotal_incl_tax, fallbackCurrency),
  appliedTaxes:
    orderTotal.taxes?.filter(isNonNullable).map((tax) => ({
      label: tax.title,
      rate: tax.rate,
      amount: toIMoney(tax.amount, fallbackCurrency),
    })) ?? [],
  discounts:
    orderTotal.discounts?.filter(isNonNullable).map((discount) => ({
      label: discount.label,
      coupon_code: discount.coupon_code ?? undefined,
      amount: toIMoney(discount.amount, fallbackCurrency),
    })) ?? [],
  shipping: orderTotal.total_shipping ? toIMoney(orderTotal.total_shipping, fallbackCurrency) : undefined,
  appliedCampaign: mapAppliedCampaign(order.loyalty_info),
});

export const mapCustomerOrderResponse = (
  response: MagentoApiGetCustomerOrderQueryResponse,
  showPricesWithTaxExcluded = false
): Order => {
  const order = response.customer?.orders?.items.find(isNonNullable);

  if (!order) {
    throw new Error("ORDER_NOT_FOUND");
  }

  const orderItems = order.items?.filter(isNonNullable) ?? [];
  const orderTotal = getRequiredValue(order.total, "ORDER_TOTAL_MISSING");
  const fallbackCurrency = getFallbackCurrency(
    orderTotal.grand_total.currency,
    orderTotal.subtotal.currency,
    orderTotal.subtotal_incl_tax.currency
  );
  const paymentMethod = order.payment_methods?.filter(isNonNullable)[0]?.type ?? "";

  return {
    orderId: atob(order.id),
    paymentMethod,
    isZeroValueOrder: paymentMethod === customPaymentMethods.zeroValueOrder,
    isSubscriptionOrder: orderItems.some((item) => item.product?.subscription === 1),
    items: orderItems.map((item) => mapOrderItem(item, showPricesWithTaxExcluded)),
    invoiceUrl: order.invoices.find(isNonNullable)?.url ?? undefined,
    shippingAddress: fromMagentoOrderAddress(order.shipping_address),
    billingAddress: fromMagentoOrderAddress(
      order.billing_address ? { ...order.billing_address, pickup_information: "" } : null
    ),
    number: order.number,
    orderDate: order.order_date,
    shippingMethod: {
      methodCode: order.shipping_method ?? "",
      carrierCode: order.carrier ?? "",
    },
    status: order.status,
    prices: mapOrderPrices(orderTotal, order, fallbackCurrency),
    total: {
      taxes:
        orderTotal.taxes?.filter(isNonNullable).map((tax) => ({
          title: tax.title,
          rate: tax.rate,
          amount: toLegacyPrice(tax.amount, fallbackCurrency),
        })) ?? [],
      total_shipping: toLegacyPrice(orderTotal.total_shipping, fallbackCurrency),
    },
    total_including_trial: order.total_including_trial
      ? mapTrialTotals(order.total_including_trial, "ORDER_TOTAL_INCLUDING_TRIAL_MISSING", fallbackCurrency)
      : undefined,
    total_after_trial: mapTrialTotals(order.total_after_trial, "ORDER_TOTAL_AFTER_TRIAL_MISSING", fallbackCurrency),
    methodAndCarrierCode: order.shipping_method_code ?? "",
    additional_attributes: order.additional_attributes.filter(isNonNullable).map((attr) => ({
      attribute_code: attr?.attribute_code ?? "",
      value: attr?.value ?? "",
    })),
  } as Order;
};
